"use client"

import React from 'react'
import TextAreaInput, { TextAreaInputProps } from '../inputs/text-area-input'
import { Control, FieldPath, FieldValues, useController } from 'react-hook-form'
import { useDirtyFields } from '../../../features/homeowners/context/dirty-fields-context'
import { useReadOnly } from '../../../features/homeowners/context/read-only-context'

/**
 * Props for the FormTextareaInput component
 * 
 * @template TFieldValues - The type of the form values object
 * @template TName - The type of the field name (must be a valid path in TFieldValues)
 */
type FormTextareaInputProps<
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> = TextAreaInputProps & {
    /** The field name in the form (must be a valid path in TFieldValues) */
    name: TName,
    /** React Hook Form control object */
    control: Control<TFieldValues>
}

/**
 * A form textarea input component that integrates with React Hook Form
 * 
 * This component renders a multi-line text input with form integration.
 * It wraps the TextAreaInput component with React Hook Form functionality.
 * 
 * @template TFieldValues - The type of the form values object
 * @template TName - The type of the field name (must be a valid path in TFieldValues)
 * 
 * @param props - Component props
 * @param props.name - The field name in the form
 * @param props.control - React Hook Form control object
 * @param props.textareaProps - Additional textarea input props
 * 
 * @example
 * ```tsx
 * // In your form component
 * const form = useForm<MyFormType>()
 * 
 * <FormTextareaInput
 *   name="description"
 *   control={form.control}
 *   placeholder="Enter your description"
 *   rows={4}
 *   inputGroupProps={{
 *     label: "Description",
 *   }}
 * />
 * ```
 * 
 * @throws {Error} When the name prop is not provided
 * 
 * @returns JSX.Element - The rendered textarea input component
 */
function FormTextareaInput<
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>(props: FormTextareaInputProps<TFieldValues, TName>) {
    const { name, control, ...inputProps } = props
    
    // Validate that name is provided as it's required for form integration
    if (!name) {
        throw new Error('name is required)')
    }

    // Get dirty field state
    const { isFieldDirty } = useDirtyFields()
    const isDirty = isFieldDirty(name as string)
    
    // Check read-only context
    const { isReadOnly } = useReadOnly()

    // Controller for form field integration
    const { field, fieldState } = useController({
        control,
        name,
    })

    const { error } = fieldState

    return (
        <TextAreaInput 
            {...props}
            name={field.name}
            onChange={(e) => field.onChange(e.currentTarget.value)}
            onBlur={field.onBlur}
            value={field.value || ""}
            ref={field.ref}
            variant={error ? 'destructive' : 'default'}
            disabled={isReadOnly || inputProps.disabled}
            inputGroupProps={{
                error: error?.message,
                isDirty: isDirty,
                ...props.inputGroupProps
            }}
        />
    )
}

export default FormTextareaInput